import type { TFunction } from "i18next";

/** Plain-language labels for the read-only Nexus tools the agent may call. */
const TOOL_LABELS: Record<string, { key: string; fallback: string }> = {
  nexus_health: { key: "askNexus.tools.health", fallback: "Checking Nexus is reachable" },
  nexus_get_my_capabilities: { key: "askNexus.tools.myCapabilities", fallback: "Checking what I can read" },
  nexus_get_nexus_capabilities: { key: "askNexus.tools.nexusCapabilities", fallback: "Reading what Nexus offers" },
  nexus_get_brand_performance: { key: "askNexus.tools.brandPerformance", fallback: "Reading brand performance" },
  nexus_get_competitive_insights: { key: "askNexus.tools.competitiveInsights", fallback: "Reading competitive insights" },
  nexus_get_industry_overview: { key: "askNexus.tools.industryOverview", fallback: "Reading industry overview" },
  nexus_get_recent_opportunities: { key: "askNexus.tools.recentOpportunities", fallback: "Reading recent opportunities" },
  nexus_list_creative_examples: { key: "askNexus.tools.creativeExamples", fallback: "Finding creative examples" },
  nexus_list_filter_options: { key: "askNexus.tools.filterOptions", fallback: "Reading filter options" },
  nexus_list_insight_slices: { key: "askNexus.tools.insightSlices", fallback: "Reading the list of insight slices" },
  nexus_list_personas: { key: "askNexus.tools.personas", fallback: "Reading your personas" },
};

const CONTEXT_MAX_PARTS = 4;
const CONTEXT_VALUE_MAX_CHARS = 48;
const LIST_PREVIEW_ITEMS = 2;

export function toolLabel(t: TFunction, tool: string): string {
  const label = TOOL_LABELS[tool];
  return label ? t(label.key, label.fallback) : t("askNexus.tools.unknown", "Using {{tool}}", { tool });
}

/**
 * A short context line from the call's actual inputs, e.g.
 * "Acme · 2026-09-01 → 2026-09-30 · Meta, TikTok". Nothing is invented: only
 * scalar and list inputs the agent sent are shown.
 */
export function toolContext(input: unknown): string {
  if (!input || typeof input !== "object" || Array.isArray(input)) return "";
  const entries = Object.entries(input as Record<string, unknown>);
  const parts: string[] = [];
  const start = findValue(entries, /^(start|from|since)(_?date)?$/i);
  const end = findValue(entries, /^(end|to|until)(_?date)?$/i);
  if (start && end) parts.push(`${start} → ${end}`);

  for (const [key, value] of entries) {
    if (parts.length >= CONTEXT_MAX_PARTS) break;
    if (start && end && /^(start|end|from|to|since|until)(_?date)?$/i.test(key)) continue;
    const text = formatValue(value);
    if (text) parts.push(text);
  }
  return parts.join(" · ");
}

function findValue(entries: Array<[string, unknown]>, key: RegExp): string | null {
  const found = entries.find(([name, value]) => key.test(name) && typeof value === "string");
  return found ? String(found[1]) : null;
}

function formatValue(value: unknown): string {
  if (typeof value === "string") return truncate(value);
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    const items = value.filter((item): item is string | number => typeof item === "string" || typeof item === "number");
    if (items.length === 0) return "";
    const shown = items.slice(0, LIST_PREVIEW_ITEMS).join(", ");
    return truncate(items.length > LIST_PREVIEW_ITEMS ? `${shown} +${items.length - LIST_PREVIEW_ITEMS}` : shown);
  }
  return "";
}

function truncate(text: string): string {
  return text.length > CONTEXT_VALUE_MAX_CHARS ? `${text.slice(0, CONTEXT_VALUE_MAX_CHARS - 1)}…` : text;
}
